/**
 * Client Portal V2 - Jobs List Page
 * ==================================
 * Operational visibility into all jobs.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientStatusBadge, ClientKpiCard, ClientEmptyState, 
  ClientLoadingState, ClientErrorState, ClientAlertBanner
} from "@/components/client/ClientComponents";
import { 
  Briefcase, Search, Filter, ChevronRight, Clock, 
  AlertTriangle, CheckCircle, Calendar, Building2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ClientJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 25, total_pages: 1 });
  const [summary, setSummary] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters from URL
  const status = searchParams.get("status") || "";
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      params.set("page", page.toString());
      params.set("page_size", "25");

      const res = await apiClient.get(`/api/client/jobs?${params}`);
      
      if (res.data?.success) {
        setJobs(res.data.data || []);
        setMeta(res.data.meta || {});
        setSummary(res.data.summary || {});
      }
    } catch (err) {
      console.error("Failed to load jobs:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      setError("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchJobs();
  }, [fetchJobs, navigate]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set("page", "1"); // Reset to page 1 on filter change
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateFilter("search", formData.get("search"));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  };

  const isOverdue = (job) => {
    if (!job.due_date) return false;
    if (["COMPLETED", "CLOSED", "CANCELLED", "REJECTED"].includes(job.stage)) return false;
    return new Date(job.due_date) < new Date();
  };

  return (
    <ClientLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Jobs</h1>
        <p className="text-slate-500 mt-1">Track all reactive and planned maintenance work</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <ClientKpiCard
          title="Total Jobs"
          value={summary.total || meta.total}
          icon={Briefcase}
          color="teal"
        />
        <ClientKpiCard
          title="Open Jobs"
          value={summary.open || 0}
          icon={Clock}
          color="blue"
          onClick={() => updateFilter("status", "open")}
        />
        <ClientKpiCard
          title="Awaiting Approval"
          value={jobs.filter(j => j.stage === "AWAITING_CLIENT_APPROVAL").length}
          icon={AlertTriangle}
          color="amber"
          alert={jobs.some(j => j.stage === "AWAITING_CLIENT_APPROVAL")}
          onClick={() => updateFilter("status", "awaiting_approval")}
        />
        <ClientKpiCard
          title="Completed"
          value={jobs.filter(j => ["COMPLETED", "CLOSED"].includes(j.stage)).length}
          icon={CheckCircle}
          color="green"
          onClick={() => updateFilter("status", "completed")}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search by reference, description, park..."
                defaultValue={search}
                className="pl-10"
              />
            </div>
          </form>

          {/* Status filter */}
          <Select value={status || "all"} onValueChange={(v) => updateFilter("status", v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear filters */}
          {(status || search) && (
            <Button
              variant="ghost"
              onClick={() => setSearchParams({})}
              className="shrink-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Jobs List */}
      {loading ? (
        <ClientLoadingState message="Loading jobs..." />
      ) : error ? (
        <ClientErrorState message={error} onRetry={fetchJobs} />
      ) : jobs.length === 0 ? (
        <ClientEmptyState
          icon={Briefcase}
          title="No jobs found"
          description={status || search ? "Try adjusting your filters" : "No jobs have been created yet"}
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-2">Reference</div>
            <div className="col-span-2">Park / Site</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Due Date</div>
            <div className="col-span-2">Quote Value</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/client-v2/jobs/${job.id}`}
                className="block hover:bg-slate-50 transition-colors"
                data-testid={`job-row-${job.id}`}
              >
                {/* Desktop Row */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-2">
                    <p className="font-medium text-slate-900">{job.job_reference}</p>
                    {job.urgent && (
                      <span className="text-xs text-orange-600 font-medium">Action Required</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-900 truncate">{job.park_name}</p>
                    {job.unit_name && (
                      <p className="text-xs text-slate-500 truncate">{job.unit_name}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700">{job.job_type || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <ClientStatusBadge stage={job.stage} label={job.stage_label} />
                  </div>
                  <div className="col-span-1">
                    <p className={`text-sm ${isOverdue(job) ? "text-red-600 font-medium" : "text-slate-600"}`}>
                      {formatDate(job.due_date)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-900 font-medium">{formatCurrency(job.quote_value)}</p>
                  </div>
                  <div className="col-span-1 text-right">
                    <ChevronRight className="w-5 h-5 text-slate-400 inline-block" />
                  </div>
                </div>

                {/* Mobile Card */}
                <div className="md:hidden p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-900">{job.job_reference}</p>
                      <p className="text-sm text-slate-600">{job.park_name}</p>
                    </div>
                    <ClientStatusBadge stage={job.stage} label={job.stage_label} />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{job.job_type}</span>
                    <span className={`font-medium ${isOverdue(job) ? "text-red-600" : "text-slate-700"}`}>
                      Due: {formatDate(job.due_date)}
                    </span>
                  </div>
                  {job.quote_value && (
                    <p className="mt-2 text-sm text-slate-900 font-medium">
                      Quote: {formatCurrency(job.quote_value)}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {meta.page} of {meta.total_pages} ({meta.total} jobs)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => updateFilter("page", String(meta.page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => updateFilter("page", String(meta.page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </ClientLayout>
  );
}
